/**
 * useChannelMeta — fetches channel metadata from the backend registry.
 *
 * Returns a Record<string, ChannelMeta> keyed by channel_type.
 * Falls back to hardcoded defaults while loading or on error,
 * so the UI never shows blank icons/labels.
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface ChannelMeta {
  label: string;
  icon: string;
  previewImage: string;
  description?: string;
  color?: string;
  order?: number;
  requiredIntegration?: string | null;
  supportsAppUrl?: boolean;
  supportsEmbed?: boolean;
  supportsTransportDisplay?: boolean;
  supportsCustomization?: boolean;
  isExternal?: boolean;
}

// Hardcoded fallback — kept in sync with backend channel definitions.
// New channels added via the registry show up automatically once the
// API responds; this fallback only covers the initial set.
const DEFAULT_CHANNEL_META: Record<string, ChannelMeta> = {
  slack: {
    label: "Slack",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/logos/slack.svg",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-slack-animated.svg",
  },
  teams: {
    label: "Teams",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/logos/msteams.png",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-teams-animated.svg",
  },
  whatsapp: {
    label: "WhatsApp",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/logos/whatsapp.svg",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-whatsapp.svg",
  },
  chat: {
    label: "Chat",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-icon.svg",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-chat-animated.svg",
  },
  form: {
    label: "Form",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-chat-logo.svg",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-form-animated.svg",
  },
  embed_widget: {
    label: "Widget",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/widget-icon.svg",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/app-form-animated.svg",
  },
  telegram: {
    label: "Telegram",
    icon: "https://storage.googleapis.com/image-storage-spot-manual/logos/external-integrations/external-integrations/telegram.png",
    previewImage:
      "https://storage.googleapis.com/image-storage-spot-manual/misc/illustrations/telegram-animated.svg",
  },
};

interface ApiChannelMeta {
  channel_type: string;
  label: string;
  icon_url: string;
  preview_image_url: string;
  description?: string;
  color?: string;
  order?: number;
  required_integration?: string | null;
  supports_app_url?: boolean;
  supports_embed?: boolean;
  supports_transport_display?: boolean;
  supports_customization?: boolean;
  is_external?: boolean;
}

function apiToChannelMeta(api: ApiChannelMeta): ChannelMeta {
  return {
    label: api.label,
    icon: api.icon_url,
    previewImage: api.preview_image_url,
    description: api.description,
    color: api.color,
    order: api.order,
    requiredIntegration: api.required_integration,
    supportsAppUrl: api.supports_app_url,
    supportsEmbed: api.supports_embed,
    supportsTransportDisplay: api.supports_transport_display,
    supportsCustomization: api.supports_customization,
    isExternal: api.is_external,
  };
}

export function useChannelMeta(): Record<string, ChannelMeta> {
  const { data } = useQuery({
    queryKey: ["channel-meta"],
    queryFn: async () => {
      const resp = await axios.get<ApiChannelMeta[]>("/api/backend/channels");
      return resp.data;
    },
    staleTime: 5 * 60 * 1000, // 5 min — channel metadata rarely changes
    retry: 1,
  });

  if (!data) return DEFAULT_CHANNEL_META;

  // When the API responds, it is the source of truth — the platform
  // component kill switch filters disabled channels server-side, and any
  // channel missing from the response must not appear in the picker.
  // The hardcoded DEFAULT_CHANNEL_META is only consulted per-channel as
  // a fallback for fields the API might leave blank (e.g. preview image).
  const result: Record<string, ChannelMeta> = {};
  for (const ch of data) {
    const fallback = DEFAULT_CHANNEL_META[ch.channel_type];
    result[ch.channel_type] = {
      ...(fallback ?? {}),
      ...apiToChannelMeta(ch),
      previewImage:
        ch.preview_image_url ||
        fallback?.previewImage ||
        DEFAULT_CHANNEL_META.chat.previewImage,
    };
  }
  return result;
}

/** For non-hook contexts (e.g., column definitions). Returns the default. */
export function getChannelMeta(channelType: string): ChannelMeta {
  return (
    DEFAULT_CHANNEL_META[channelType] ?? {
      label: channelType,
      icon: "",
      previewImage: DEFAULT_CHANNEL_META.chat.previewImage,
    }
  );
}

export { DEFAULT_CHANNEL_META as CHANNEL_META };
